// Implements: F-UIX-019
/**
 * The range of the platform totals: two days, either of which may stay empty for the
 * server's default (the 366 days ending today). The server checks the range and answers a
 * bad one with its own sentence, which the view shows; the form only collects it.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import styles from "./PlatformTotals.module.css";

export interface RangeDraft {
  readonly from: string;
  readonly to: string;
}

export interface RangeFormProps {
  readonly draft: RangeDraft;
  readonly busy: boolean;
  readonly onChange: (draft: RangeDraft) => void;
  readonly onSubmit: () => void;
}

export function RangeForm({ draft, busy, onChange, onSubmit }: RangeFormProps): ReactElement {
  const { t } = useTranslation();
  const ids = useId();
  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <fieldset className={styles.range} aria-describedby={`${ids}-hint`}>
        <legend className={styles.legend}>{t("admin.totals.rangeLabel")}</legend>
        <div className={styles.field}>
          <label htmlFor={`${ids}-from`}>{t("admin.totals.from")}</label>
          <input
            id={`${ids}-from`}
            className={styles.date}
            type="date"
            value={draft.from}
            onChange={(event) => {
              onChange({ ...draft, from: event.target.value });
            }}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor={`${ids}-to`}>{t("admin.totals.to")}</label>
          <input
            id={`${ids}-to`}
            className={styles.date}
            type="date"
            value={draft.to}
            onChange={(event) => {
              onChange({ ...draft, to: event.target.value });
            }}
          />
        </div>
        <Button variant="secondary" type="submit" loading={busy} disabled={busy}>
          {t("admin.totals.apply")}
        </Button>
        <p id={`${ids}-hint`} className={styles.hint}>
          {t("admin.totals.rangeHint")}
        </p>
      </fieldset>
    </form>
  );
}
